import { CaretDown, CaretUp, ClipboardText } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { cap, chipZone, GRADE_SHORT, worstSeverity } from './helpers';
import w from './Workspace.module.css';
import type { Scenario } from '../engine/summation';
import { temperatureClassFor } from '../engine';
import { useDerived } from '../state/derived';
import { useStore, type DockTab, type Selection } from '../state/store';
import { formatNumber, fullPrecision } from '../ui/format';
import { Button, Ref, StatusChip, ZoneChip } from '../ui/kit';

const n = (v: number | null | undefined) => <span title={fullPrecision(v)}>{formatNumber(v)}</span>;

function Th({ children, num, unit }: { children?: ReactNode; num?: boolean; unit?: string }) {
  return <th scope="col" className={num ? w.num : undefined}>{children}{unit && <span className={w.unit}> {unit}</span>}</th>;
}

function useRowProps() {
  const selection = useStore((s) => s.selection);
  const select = useStore((s) => s.select);
  const requestFocus = useStore((s) => s.requestFocus);
  return (sel: NonNullable<Selection>) => ({
    'aria-selected': selection?.id === sel.id,
    onClick: () => select(sel),
    onDoubleClick: () => { select(sel); requestFocus(); },
    tabIndex: 0,
    onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter') { select(sel); requestFocus(); } },
  });
}

function Schedule() {
  const doc = useStore((s) => s.doc)!;
  const d = useDerived();
  const row = useRowProps();
  const areaName = new Map(doc.areas.map((a) => [a.id, a.name]));
  if (!doc.sources.length) return <p className={w.empty}>No sources of release yet. Press S to add one to the selected area.</p>;
  return (
    <table className={w.table}>
      <thead>
        <tr>
          <Th>Tag</Th><Th>Description</Th><Th>Area</Th><Th>Grade</Th>
          <Th num unit="kg/s">Wg</Th><Th num unit="m³/s">Qc</Th><Th num unit="m/s">uw</Th>
          <Th>Dilution</Th><Th>Avail.</Th><Th>Zone</Th><Th num unit="m">r horiz.</Th><Th num unit="m">r vert.</Th><Th>Group</Th><Th />
        </tr>
      </thead>
      <tbody>
        {doc.sources.map((s) => {
          const r = d?.results.sources[s.id];
          const sev = r ? worstSeverity(r.issues) : null;
          const room = r?.extent?.basis === 'room';
          return (
            <tr key={s.id} {...row({ kind: 'source', id: s.id })}>
              <td className={w.mono}>{s.tag}</td>
              <td className={w.desc}>{s.description}</td>
              <td>{areaName.get(s.areaId) ?? '—'}</td>
              <td title={cap(s.grade)}>{GRADE_SHORT[s.grade]}</td>
              <td className={w.num}>{n(r?.release?.massRate)}</td>
              <td className={w.num}>{n(r?.qc)}</td>
              <td className={w.num}>{n(r?.uw)}</td>
              <td>{cap(r?.dilution)}</td>
              <td>{cap(r?.availability)}</td>
              <td>{r?.zoneLabel ? <ZoneChip zone={chipZone(r)} label={r.zoneLabel} /> : '—'}</td>
              <td className={w.num}>{room ? 'room' : n(r?.extent?.horizontalM)}</td>
              <td className={w.num}>{room ? 'room' : n(r?.extent?.verticalM)}</td>
              <td>{[r?.gasGroup, r?.tClass].filter(Boolean).join(' ') || '—'}</td>
              <td>{sev === 'error' ? <StatusChip status="error" label="Error" /> : sev === 'warning' ? <StatusChip status="warning" label="Check" /> : null}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Checks() {
  const doc = useStore((s) => s.doc)!;
  const d = useDerived();
  const select = useStore((s) => s.select);
  const requestFocus = useStore((s) => s.requestFocus);
  if (!d) return null;
  type Item = { key: string; status: 'fail' | 'error' | 'warning' | 'info'; target: string; sel: NonNullable<Selection>; text: string; ref?: string };
  const items: Item[] = [];
  for (const e of doc.equipment) {
    const c = d.checks[e.id];
    if (!c || c.status === 'pass' || c.status === 'outside') continue;
    for (const t of c.reasons) items.push({ key: `eq:${e.id}:${t}`, status: c.status === 'fail' ? 'fail' : 'warning', target: e.tag, sel: { kind: 'equipment', id: e.id }, text: t });
  }
  for (const s of doc.sources) {
    for (const i of d.results.sources[s.id]?.issues ?? []) {
      if (i.severity === 'info') continue;
      items.push({ key: `src:${s.id}:${i.code}`, status: i.severity, target: s.tag, sel: { kind: 'source', id: s.id }, text: i.message, ...(i.ref ? { ref: [i.ref.clause, i.ref.md].filter(Boolean).join(' · ') } : {}) });
    }
  }
  for (const a of doc.areas) for (const i of d.results.areas[a.id]?.issues ?? []) items.push({ key: `area:${a.id}:${i.code}`, status: i.severity, target: a.name, sel: { kind: 'area', id: a.id }, text: i.message });
  for (const l of d.lints) {
    const t = l.targets[0]!;
    const isSource = doc.sources.some((s) => s.id === t);
    items.push({ key: l.id, status: l.severity, target: l.code, sel: { kind: isSource ? 'source' : 'area', id: t }, text: l.message, ref: [l.ref.clause, l.ref.md].filter(Boolean).join(' · ') });
  }
  const order = { fail: 0, error: 0, warning: 1, info: 2 } as const;
  items.sort((a, b) => order[a.status] - order[b.status]);
  if (!items.length) return <p className={w.empty}>No issues. {doc.sources.length} sources and {doc.equipment.length} equipment items checked.</p>;
  return (
    <ul className={w.checkList}>
      {items.map((i) => (
        <li key={i.key}>
          <button type="button" onClick={() => { select(i.sel); requestFocus(); }}>
            <StatusChip status={i.status} label={{ fail: 'Fail', error: 'Error', warning: 'Check', info: 'Note' }[i.status]} />
            <span className={w.mono}>{i.target}</span>
            <span className={w.checkText}>{i.text}</span>
            {i.ref && <Ref>{i.ref}</Ref>}
          </button>
        </li>
      ))}
    </ul>
  );
}

function Summation() {
  const doc = useStore((s) => s.doc)!;
  const d = useDerived();
  const tag = new Map(doc.sources.map((s) => [s.id, s.tag]));
  const rooms = doc.areas.filter((a) => a.kind === 'indoor');
  if (!rooms.length) return <p className={w.empty}>Summation applies to indoor areas (B.4). This project has none.</p>;
  const label = { continuous: 'Continuous', primary: 'Primary + continuous', secondary: 'Secondary + primary + continuous' } as const;
  return (
    <table className={w.table}>
      <thead>
        <tr><Th>Area</Th><Th>Case</Th><Th>Sources</Th><Th num unit="kg/s">ΣWg</Th><Th num unit="m³/s">ΣQg</Th><Th num unit="m³/s">ΣQc</Th><Th num unit="vol/vol">Xb</Th><Th num unit="vol/vol">Xcrit</Th><Th>Background</Th></tr>
      </thead>
      <tbody>
        {rooms.flatMap((a) => {
          const sc = d?.results.areas[a.id]?.scenarios ?? {};
          const rows = (Object.entries(sc) as Array<[keyof typeof label, Scenario]>);
          if (!rows.length) return [<tr key={a.id}><td>{a.name}</td><td colSpan={8} className={w.muted}>No sources yet</td></tr>];
          return rows.map(([g, s]) => (
            <tr key={`${a.id}:${g}`}>
              <td>{a.name}</td><td>{label[g]}</td>
              <td className={w.desc}>{s.members.map((m) => tag.get(m)).join(', ')}</td>
              <td className={w.num}>{n(s.sumMassRate)}</td><td className={w.num}>{n(s.sumVolumetricRate)}</td><td className={w.num}>{n(s.sumQc)}</td>
              <td className={w.num}>{n(s.xb)}</td><td className={w.num}>{n(s.xcrit)}</td>
              <td>{s.exceeds ? <StatusChip status="warning" label="Above Xcrit: low dilution" /> : <span className={w.muted}>Below Xcrit</span>}</td>
            </tr>
          ));
        })}
      </tbody>
    </table>
  );
}

function EquipmentSchedule() {
  const doc = useStore((s) => s.doc)!;
  const d = useDerived();
  const row = useRowProps();
  if (!doc.equipment.length) return <p className={w.empty}>No equipment yet. Press E to add an item, then enter its Ex marking.</p>;
  return (
    <table className={w.table}>
      <thead><tr><Th>Tag</Th><Th>Description</Th><Th>Marking</Th><Th>EPL</Th><Th>Group</Th><Th>T-class</Th><Th>In zone</Th><Th>Needs</Th><Th>Result</Th></tr></thead>
      <tbody>
        {doc.equipment.map((e) => {
          const c = d?.checks[e.id];
          const ex = e.ex.kind === 'ex' ? e.ex : null;
          return (
            <tr key={e.id} {...row({ kind: 'equipment', id: e.id })}>
              <td className={w.mono}>{e.tag}</td><td className={w.desc}>{e.description}</td>
              <td className={w.mono}>{ex ? ex.marking ?? '—' : 'Not Ex'}</td>
              <td>{ex?.epl ?? '—'}</td><td>{ex?.gasGroup ?? '—'}</td><td>{ex?.tClass ?? '—'}</td>
              <td>{c?.required ? <ZoneChip zone={c.required.zone} label={`Zone ${c.required.zone}`} /> : <span className={w.muted}>None</span>}</td>
              <td>{c?.required ? `${c.required.epl}, ${c.required.gasGroup ?? '?'}, ${c.required.aitC !== undefined ? `${temperatureClassFor(c.required.aitC) ?? ''}` : '?'}` : '—'}</td>
              <td>{c && <StatusChip status={c.status} label={{ pass: 'Suitable', fail: 'Not suitable', warning: 'Check', outside: 'Outside zones' }[c.status]} />}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Substances() {
  const doc = useStore((s) => s.doc)!;
  const row = useRowProps();
  return (
    <table className={w.table}>
      <thead><tr><Th>Name</Th><Th>State</Th><Th num unit="kg/kmol">M</Th><Th num unit="% vol">LFL</Th><Th num unit="°C">AIT</Th><Th>Group</Th><Th>T-class</Th><Th num>Rel. density</Th><Th>Data source</Th></tr></thead>
      <tbody>
        {doc.substances.map((s) => (
          <tr key={s.id} {...row({ kind: 'substance', id: s.id })}>
            <td>{s.name}</td><td>{cap(s.state.replace('-', ' '))}</td>
            <td className={w.num}>{n(s.molarMassKgPerKmol)}</td><td className={w.num}>{n(s.lflVolFrac * 100)}</td><td className={w.num}>{n(s.aitC)}</td>
            <td>{s.gasGroup ?? '—'}</td><td>{temperatureClassFor(s.aitC) ?? '—'}</td><td className={w.num}>{n(s.relativeVapourDensity)}</td>
            <td className={w.desc}>{s.dataSources?.all ?? <span className={w.muted}>Not recorded</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Dock() {
  const tab = useStore((s) => s.view.dockTab);
  const open = useStore((s) => s.view.dockOpen);
  const setView = useStore((s) => s.setView);
  const doc = useStore((s) => s.doc);
  const d = useDerived();
  if (!doc) return null;
  const fails = d ? Object.values(d.checks).filter((c) => c.status === 'fail').length : 0;
  const problems = d ? fails + Object.values(d.results.sources).reduce((k, r) => k + r.issues.filter((i) => i.severity !== 'info').length, 0) + d.lints.length : 0;
  const tabs: Array<{ id: DockTab; label: string; count?: number; alert?: boolean }> = [
    { id: 'schedule', label: 'Schedule', count: doc.sources.length },
    { id: 'checks', label: 'Checks', count: problems, alert: fails > 0 },
    { id: 'summation', label: 'Summation' },
    { id: 'equipment', label: 'Equipment', count: doc.equipment.length },
    { id: 'substances', label: 'Substances', count: doc.substances.length },
  ];
  return (
    <section className={w.dock} data-open={open} aria-label="Schedule and checks">
      <div className={w.tabs} role="tablist">
        {tabs.map((t) => (
          <button key={t.id} role="tab" type="button" aria-selected={tab === t.id} className={w.tab} onClick={() => setView({ dockTab: t.id, dockOpen: true })}>
            {t.label}{t.count !== undefined && <span className={w.tabCount} data-alert={t.alert}>{t.count}</span>}
          </button>
        ))}
        <span className={w.spacer} />
        <Button variant="ghost" icon={<ClipboardText size={14} />} onClick={() => useStore.getState().setBulk(true)} title="Paste a block of rows from a spreadsheet">Paste rows</Button>
        <Button variant="ghost" icon={open ? <CaretDown size={14} /> : <CaretUp size={14} />} aria-label={open ? 'Collapse' : 'Expand'} onClick={() => setView({ dockOpen: !open })} />
      </div>
      {open && (
        <div className={w.dockBody}>
          {tab === 'schedule' && <Schedule />}
          {tab === 'checks' && <Checks />}
          {tab === 'summation' && <Summation />}
          {tab === 'equipment' && <EquipmentSchedule />}
          {tab === 'substances' && <Substances />}
        </div>
      )}
    </section>
  );
}
